// Frontend property cache.
//
// We keep a small, exact-match map of `cleanedContent → properties` in
// localStorage so newly typed items (e.g. "milk") can show their previously
// learned properties (department, emoji, …) instantly while the server
// confirms via the persistent backend cache.
//
// This is intentionally simple: exact match on a normalized key, no fuzzy
// substring matching — that previously caused "salad" to match
// "Caesar salad" and inherit the wrong properties.

const normalize = (s) => (s == null ? '' : String(s).toLowerCase().trim());

const STRIP_DYNAMIC = ['qty', 'quantity', 'date', 'priority', 'urgency', 'scheduled_time', 'due_date'];

/** Look up cached properties for an exact (normalized) item content. */
export const lookupCached = (content, propertyCache) => {
  const key = normalize(content);
  if (!key) return null;
  const hit = propertyCache?.[key];
  return hit && Object.keys(hit).length > 0 ? hit : null;
};

/** Return a new property cache map with `content`'s properties added/merged. */
export const addToPropertyCache = (propertyCache, content, properties) => {
  const key = normalize(content);
  if (!key || !properties || Object.keys(properties).length === 0) return propertyCache;

  // Strip dynamic properties before caching — they shouldn't be reused.
  const staticOnly = {};
  for (const [k, v] of Object.entries(properties)) {
    if (STRIP_DYNAMIC.includes(k)) continue;
    if (v === null || v === undefined || v === '') continue;
    staticOnly[k] = v;
  }
  if (Object.keys(staticOnly).length === 0) return propertyCache;

  return { ...(propertyCache || {}), [key]: { ...(propertyCache?.[key] || {}), ...staticOnly } };
};

/** Bulk: walk an items array and fold their properties into the cache. */
export const learnFromItems = (items, propertyCache) => {
  let next = propertyCache || {};
  for (const item of items || []) {
    if (!item?.content || !item?.properties) continue;
    next = addToPropertyCache(next, item.content, item.properties);
  }
  return next;
};
